import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useCreateCashTransfer } from "hooks/useCreateCashTransfer";
import { useMutation } from "hooks/useMutation";
import { Loader } from "components/Loader";
import { ChervonRightIcon } from "assets/icons";
import { TransferFundsModalContainer } from "./TransferFundsModal.styles";
import { TransactionModal } from "../TransactionModal";
import { TransactionConfirmationModal } from "../TransactionConfirmationModal";
import { TransactionSuccessModal } from "../TransactionSuccessModal";
import { createCashTransfer } from "features/SurmountAccount/service";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";

const TransferFundsModal = ({
  show,
  onHide,
  onDepositClick,
  onWithdrawClick,
}) => {
  const dispatch = useDispatch();
  const { accountDetails, isLoadingAccountDetails, refetchAccountDetails } =
    useCreateCashTransfer();
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDepositConfirmationModal, setShowDepositConfirmationModal] =
    useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showWithdrawConfirmationModal, setShowWithdrawConfirmationModal] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState({
    type: "deposit",
    amount: 0,
  });
  const [depositData, setDepositData] = useState(null);
  const [withdrawData, setWithdrawData] = useState(null);

  // Deposit Mutation
  const depositMutation = useMutation({
    mutationFn: (payload) => createCashTransfer(payload),
    onSuccess: () => {
      // Close confirmation modal and show success modal
      setShowDepositConfirmationModal(false);
      setShowDepositModal(false);
      setSuccessData({ type: "deposit", amount: depositData.amount });
      setShowSuccessModal(true);
      refetchAccountDetails();
    },
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body:
            parseErrorMessage(error) ||
            "Failed to process deposit. Please try again.",
        })
      );
    },
  });

  // Withdraw Mutation
  const withdrawMutation = useMutation({
    mutationFn: (payload) => createCashTransfer(payload),
    onSuccess: () => {
      // Close confirmation modal and show success modal
      setShowWithdrawConfirmationModal(false);
      setShowWithdrawModal(false);
      setSuccessData({ type: "withdraw", amount: withdrawData.amount });
      setShowSuccessModal(true);
      refetchAccountDetails();
    },
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body:
            parseErrorMessage(error) ||
            "Failed to process withdrawal. Please try again.",
        })
      );
    },
  });

  const handleDepositClick = () => {
    if (onDepositClick) {
      onDepositClick(accountDetails);
    } else {
      // Open the deposit modal
      setShowDepositModal(true);
    }
  };

  const handleDepositModalHide = () => {
    setShowDepositModal(false);
  };

  const handleDepositNext = (data) => {
    setDepositData(data);
    setShowDepositModal(false);
    setShowDepositConfirmationModal(true);
  };

  const handleDepositConfirmationHide = () => {
    setShowDepositConfirmationModal(false);
    // Optionally go back to deposit modal
    // setShowDepositModal(true);
  };

  const handleDepositConfirm = () => {
    const payload = {
      origin_account: "plaid_bank",
      destination_account: "alpaca",
      amount: parseFloat(depositData.amount),
    };
    depositMutation.mutate(payload);
  };

  const handleWithdrawClick = () => {
    if (onWithdrawClick) {
      onWithdrawClick(accountDetails);
    } else {
      // Open the withdraw modal
      setShowWithdrawModal(true);
    }
  };

  const handleWithdrawModalHide = () => {
    setShowWithdrawModal(false);
  };

  const handleWithdrawNext = (data) => {
    setWithdrawData(data);
    setShowWithdrawModal(false);
    setShowWithdrawConfirmationModal(true);
  };

  const handleWithdrawConfirmationHide = () => {
    setShowWithdrawConfirmationModal(false);
    // Optionally go back to withdraw modal
    // setShowWithdrawModal(true);
  };

  const handleWithdrawConfirm = () => {
    const payload = {
      origin_account: "alpaca",
      destination_account: "plaid_bank",
      amount: parseFloat(withdrawData.amount),
    };
    withdrawMutation.mutate(payload);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Close the main transfer modal
    onHide();
  };

  return (
    <>
      <TransferFundsModalContainer
        show={
          show &&
          !showDepositModal &&
          !showDepositConfirmationModal &&
          !showWithdrawModal &&
          !showWithdrawConfirmationModal &&
          !showSuccessModal
        }
        onHide={onHide}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            Transfer funds to or from Surmount account.
          </h2>
        </div>

        <div className="modal-body-content">
          {isLoadingAccountDetails ? (
            <div className="loader-wrapper">
              <Loader />
            </div>
          ) : (
            <>
              {/* Deposit Section */}
              <div className="transfer-section" onClick={handleDepositClick}>
                <div className="transfer-section-left">
                  <div className="transfer-content">
                    <h3 className="transfer-title">Deposit</h3>
                    <p className="transfer-description">
                      Deposit funds from your bank to your Surmount account.
                    </p>
                  </div>
                </div>
                <div className="transfer-section-right">
                  <ChervonRightIcon />
                </div>
              </div>
              <div className="divider" />
              {/* Withdraw Section */}
              <div className="transfer-section" onClick={handleWithdrawClick}>
                <div className="transfer-section-left">
                  <div className="transfer-content">
                    <h3 className="transfer-title">Withdraw</h3>
                    <p className="transfer-description">
                      Transfer money from your Surmount account to your bank.
                    </p>
                  </div>
                </div>
                <div className="transfer-section-right">
                  <ChervonRightIcon />
                </div>
              </div>
            </>
          )}
        </div>
      </TransferFundsModalContainer>

      <TransactionModal
        show={showDepositModal}
        onHide={handleDepositModalHide}
        onNext={handleDepositNext}
        accountDetails={accountDetails}
        type="deposit"
      />

      <TransactionConfirmationModal
        show={showDepositConfirmationModal}
        onHide={handleDepositConfirmationHide}
        onConfirm={handleDepositConfirm}
        transactionData={depositData}
        isLoading={depositMutation.isLoading}
        type="deposit"
      />

      <TransactionModal
        show={showWithdrawModal}
        onHide={handleWithdrawModalHide}
        onNext={handleWithdrawNext}
        accountDetails={accountDetails}
        type="withdraw"
      />

      <TransactionConfirmationModal
        show={showWithdrawConfirmationModal}
        onHide={handleWithdrawConfirmationHide}
        onConfirm={handleWithdrawConfirm}
        transactionData={withdrawData}
        isLoading={withdrawMutation.isLoading}
        type="withdraw"
      />

      <TransactionSuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        type={successData.type}
        amount={successData.amount}
      />
    </>
  );
};

TransferFundsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onDepositClick: PropTypes.func,
  onWithdrawClick: PropTypes.func,
};

export { TransferFundsModal };
